import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { setNextSong, setCurrentSong } from '../actions';
import soundtrack1 from './TheEcstasyOfGold.mp3';
import soundtrack2 from './TuesdayAfternoon.mp3';
import soundtrack3 from './HorseWithNoName.mp3';
import soundtrack4 from './CatStevens.mp3';

const songsArray = ['1', '2', '3', '4'];
const mp3Map = {
  1: soundtrack1,
  2: soundtrack2,
  3: soundtrack3,
  4: soundtrack4
};

const FADE_DURATION_MS = 1000; // Total duration for fade-out effect in milliseconds
const FADE_STEPS = 50;         // Number of steps for fade-out effect, more steps = smoother fade but more CPU usage, adjust as needed.

//This custom hook is an Improvement over SoundPlayerAIVersion_1.js
// Custom hook to manage persistent audio element and fading by using useRef to store the audio element
// and fade timeout, and useEffect to initialize the audio element once and clean up on unmount. 
// The fadeOut function is memoized with useCallback to prevent unnecessary re-creations on each render.
// By placing this outside the SoundPlayer component, we can perform the fade out when the component unmounts or when 
// the user toggles play/pause, ensuring a smooth transition without abrupt stops in the music.
const useAudio = () => {
  const audioRef = useRef(null);
  const fadeTimeoutRef = useRef(null);
  const [audioReady, setAudioReady] = useState(false);

  useEffect(() => {
    // Initialize audio element once.
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'none';
    }
    setAudioReady(true);

    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, []);

  const fadeOut = useCallback(() => { // Fade out the current audio before pausing, useCallback to memoize the function and prevent unnecessary re-creations
    const audio = audioRef.current;
    if (!audio || audio.paused) return; // No need to fade if already paused
    if (fadeTimeoutRef.current) return; // Prevent multiple fade-outs at the same time

    const volumeDecrement = 1 / FADE_STEPS;
    const stepDuration = FADE_DURATION_MS / FADE_STEPS;
    let step = 0;

    const fade = () => {
      if (step < FADE_STEPS && audio.volume > 0) {
        audio.volume = Math.max(0, audio.volume - volumeDecrement);
        step++;
        fadeTimeoutRef.current = setTimeout(fade, stepDuration);
      } else {
        audio.pause();
        audio.volume = 1;
        fadeTimeoutRef.current = null;
      }
    };

    fade();    // Start the fade-out process
  }, []);      // Empty dependency array means this function will be created once and won't change, which is ideal for a stable fadeOut function that doesn't rely on changing props or state.
  // Return the audio ref and fadeOut function as an object for use in the component by destructuring.
  return { audioRef, fadeOut, audioReady };
};
// Reminder, props originate from the Redux store via connect() and mapStateToProps, and action creators are also passed in as props.
const SoundPlayer = (props) => {
  const { setNextSong, setCurrentSong } = props;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [canHearMusic, setCanHearMusic] = useState(false);
  const [playbackError, setPlaybackError] = useState('');
  const { audioRef, fadeOut, audioReady } = useAudio();
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  const hasValidTrack = Number.isInteger(props.nextSong)
    && props.nextSong >= 0
    && props.nextSong < songsArray.length;

  useEffect(() => {
    // Pick the random starting track in Redux without assigning an audio source.
    setCurrentSong(songsArray);
  }, [setCurrentSong]);

  // Track whether the audio element is actually playing and audible
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateAudibleState = () => {
      setCanHearMusic(!audio.paused && !audio.muted && audio.volume > 0);
    };

    const handlePauseOrEnd = () => {
      setCanHearMusic(false);
    };

    audio.addEventListener('playing', updateAudibleState);
    audio.addEventListener('pause', handlePauseOrEnd);
    audio.addEventListener('ended', handlePauseOrEnd);
    audio.addEventListener('volumechange', updateAudibleState);

    // Initialize state in case the audio is already playing on mount
    updateAudibleState();

    return () => {
      audio.removeEventListener('playing', updateAudibleState);
      audio.removeEventListener('pause', handlePauseOrEnd);
      audio.removeEventListener('ended', handlePauseOrEnd);
      audio.removeEventListener('volumechange', updateAudibleState);
    };
  }, [audioRef]);

  // Load the reducer-selected next track only while playback is active.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isPlayingRef.current || !hasValidTrack) return;

    const source = mp3Map[songsArray[props.nextSong]];
    audio.src = source;
    audio.volume = 1;
    audio.play().catch(error => {
      console.error('Playback failed:', error);
      setPlaybackError(`${error.name || 'Error'}: ${error.message || error}`);
      setIsStarting(false);
      setIsPlaying(false);
    });
  }, [props.nextSong, audioRef, hasValidTrack]);

  // Handle play/pause and event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setNextSong(songsArray); // Advance Redux; the selected-track effect loads and plays the next song.
    };

    if (isPlaying) {
      audio.volume = 1;
      audio.addEventListener('ended', handleEnded);
    } else {
      fadeOut();
      audio.removeEventListener('ended', handleEnded);
    }

    return () => {
      audio.removeEventListener('ended', handleEnded);
      // Fade out when component unmounts or isPlaying changes to false
      if (isPlaying && audio && !audio.paused) {
        fadeOut();
      }
    };
  }, [isPlaying, setNextSong, audioRef, fadeOut]);

  const clickLockedRef = useRef(false);
  const isStartingRef = useRef(false);

  const togglePlay = () => {
    if (!hasValidTrack || clickLockedRef.current || isStartingRef.current) return;

    clickLockedRef.current = true;
    window.setTimeout(() => {
      clickLockedRef.current = false;
    }, FADE_DURATION_MS);

    if (isPlaying) {
      setIsStarting(false);
      setIsPlaying(false);
      // Advance Redux so the next Play press starts the following song.
      setNextSong(songsArray);
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    audio.src = mp3Map[songsArray[props.nextSong]];
    audio.volume = 1;
    setPlaybackError('');
    isStartingRef.current = true;
    setIsStarting(true);
    audio.play().then(() => {
      isStartingRef.current = false;
      setIsStarting(false);
      setIsPlaying(true);
    }).catch(error => {
      console.error('Playback failed:', error);
      isStartingRef.current = false;
      setIsStarting(false);
      setPlaybackError(`${error.name || 'Error'}: ${error.message || error}`);
      setIsPlaying(false);
    });
  };

  let content = <button>Loading...</button>;
  if (props.imagesLoaded && props.spinnerIsHidden && hasValidTrack && audioReady) {
    content = <button className="music-play-button" type="button" onClick={togglePlay}>{isStarting || (isPlaying && !canHearMusic) ? 'Loading Music' : (isPlaying ? 'Stop Music' : 'Play Music')}</button>;
  }

  return (
    <div className="app">
      {content}
      {playbackError && <div role="alert">Music playback failed: {playbackError}</div>}
    </div>
  );
};
// Map Redux state to component props
const mapStateToProps = (state) => {
  return {
    nextSong: state.getNextSongReducer.nextSong,
    imagesLoaded: state.imagesLoaded,
    spinnerIsHidden: state.spinnerVisibility.slideshowSpinnerIsHidden
  };
};
//NOTE action creator(s) included here
export default connect(mapStateToProps, { setNextSong, setCurrentSong })(SoundPlayer);

//Note about audioRef and fadeOut: By using useRef to store the audio element, we ensure that the same audio instance 
// is used across renders, allowing us to maintain state like current time and volume. The fadeOut function is memoized with 
// useCallback to prevent unnecessary re-creations on each render, which is important for performance and to ensure that the 
// fade-out process is consistent whenever it is triggered, whether by a user action or when the component unmounts.
// Even though fadeOut is memoized with useCallback([]) and is stable, ESLint will still expect it in the dependency list 
// because it’s referenced inside the effect. Keeping it there avoids warnings and has no downside.


//Why not remove audioRef from the dependency array of the useEffect hooks? Because even though audioRef.current is stable, 
// the ref object itself is not guaranteed to be stable across renders. ESLint's react-hooks/exhaustive-deps rule will warn 
// if you reference a variable inside an effect but don't list it in the dependency array, and it doesn't understand that refs 
// are stable. So keeping audioRef in the dependency array is necessary to satisfy ESLint, even though in practice it won't 
// cause issues because the ref is stable.
//ESLint’s react-hooks/exhaustive-deps warns whenever you reference a variable inside an effect but don’t list it in 
// the dependency array. It doesn’t “understand” that refs are stable and won’t change. So it’s not really future‑proofing 
// audioRef; it’s enforcing a blanket rule.

//In practice:

//audioRef is stable across renders, so leaving it out is fine.
//The rule still flags it because it can’t prove stability.
//So the warning is mostly mechanical, not because there’s a real bug. Keeping audioRef in deps is the simplest way to keep ESLint happy.
